/**
 * Pure helpers that turn pose / zone state into HudState pieces (testable in Node).
 * Messages are constant objects so the per-frame path allocates nothing.
 */
import type { GpsGate, HudMessage, PoseState, RenderMode, TrackingState, ZoneStatus } from '../contracts';
import type { PoseDiagnostics } from '../pose';

export const MESSAGES = {
  gpsDenied: { kind: 'error', text: 'Standortzugriff verweigert. Bitte in den Browser-Einstellungen erlauben.' },
  gpsUnavailable: { kind: 'warn', text: 'Kein GPS-Signal' },
  trackingLost: { kind: 'warn', text: 'AR-Tracking verloren – Kamera auf Ufer/Umgebung richten' },
  noCompass: {
    kind: 'warn',
    text: 'Kompass nicht verfügbar – Sensor-Berechtigung in Chrome prüfen (Website-Einstellungen → Bewegungssensoren)',
  },
  gpsDegraded: { kind: 'warn', text: 'GPS ungenau: Overlay abgedunkelt' },
  noData: { kind: 'info', text: 'Keine amtlichen Daten (ausserhalb Kanton Luzern)' },
} as const satisfies Record<string, HudMessage>;

export type PositionErrorState = 'denied' | 'unavailable' | null;

export interface MessageInput {
  mode: RenderMode;
  gpsGate: GpsGate;
  tracking: TrackingState;
  /** No orientation provider works at all (AbsoluteOrientationSensor, deviceorientationabsolute, deviceorientation). */
  noCompass: boolean;
  positionError: PositionErrorState;
  zone: ZoneStatus | null;
}

/** Highest-priority HUD message, or null. */
export function pickMessage(i: MessageInput): HudMessage | null {
  if (i.positionError === 'denied') return MESSAGES.gpsDenied;
  if (i.mode === 'xr' && i.tracking === 'lost') return MESSAGES.trackingLost;
  if (i.noCompass) return MESSAGES.noCompass;
  if (i.positionError === 'unavailable' && i.gpsGate === 'waiting') return MESSAGES.gpsUnavailable;
  if (i.gpsGate === 'degraded') return MESSAGES.gpsDegraded;
  if (i.gpsGate !== 'waiting' && i.zone?.zone === 'nodata') return MESSAGES.noData;
  return null;
}

/** Target overlay alpha: hidden until the GPS gate opens and on tracking loss, dimmed when degraded. */
export function overlayAlpha(gate: GpsGate, tracking: TrackingState): number {
  if (gate === 'waiting' || tracking === 'lost') return 0;
  return gate === 'degraded' ? 0.5 : 1;
}

/** Zone for the HUD: 'nodata' has no meaningful shore/limit distances (other canton). */
export function hudZone(z: ZoneStatus): ZoneStatus {
  return z.zone === 'nodata' ? { ...z, shore: null, limit: null } : z;
}

const f = (v: number | null | undefined, d = 1): string =>
  v === null || v === undefined || !Number.isFinite(v) ? '–' : v.toFixed(d);

export interface DebugExtras {
  lakeId: string | null;
  fps: number;
  drawnTriangles: number;
  positionSource: string;
  hudRotationDeg: number;
  /** Orientation provider status text, e.g. 'startet …' | 'ok' | 'fehlgeschlagen'. */
  orientationStatus: string;
  /** Last orientation provider error message, if any (B.6: so a field test shows why the compass fails). */
  orientationError: string | null;
}

/** Key/value rows for the raw debug table (built at <= 2 Hz, allocation is fine). */
export function buildDebugRows(p: PoseState, d: PoseDiagnostics, x: DebugExtras): [string, string][] {
  const ll = p.userLatLon;
  return [
    ['Modus', `${p.mode} · ${p.tracking}`],
    ['Pos', ll ? `${ll.lat.toFixed(6)}, ${ll.lon.toFixed(6)}` : '–'],
    ['ENU', p.userENU ? `${p.userENU.e.toFixed(1)} / ${p.userENU.n.toFixed(1)}` : '–'],
    ['GPS', `${f(p.gpsAccuracy)} m · ${p.gpsGate} · σ ${f(d.kalmanSigmaM)} · ${x.positionSource}`],
    ['Speed', `${f(p.speedKmh)} km/h (${d.speedSource ?? '–'}) · COG ${f(p.courseDeg, 0)}`],
    ['Heading', `${f(p.headingDeg)}° · Pitch ${f(p.pitchDeg)}°`],
    ['Ori', `${d.orientationSource ?? '–'} · Status ${x.orientationStatus} · n ${d.orientationSampleCount} · ${f(d.orientationAgeMs, 0)} ms`],
    ['Ori-Fehler', x.orientationError ?? '–'],
    ['Ori-Roh', `${f(d.rawHeadingMagDeg)}° mag · Pitch ${f(d.rawPitchDeg)}°`],
    ['Yaw', `meas ${f(d.yawMeasDeg)} · filt ${f(d.yawFilteredDeg)} · n ${d.yawSamples}${d.yawFilterFast ? ' fast' : ''}`],
    ['XR', `age ${f(d.xrPoseAgeMs, 0)} ms · HUD ${x.hudRotationDeg}° (raw ${f(d.hudRawDeg, 0)})`],
    ['Align', p.alignment ? `α ${f((p.alignment.yawRad * 180) / Math.PI)}° y ${f(p.alignment.waterY, 2)}` : '–'],
    ['Decl', `${f(d.declinationDeg, 2)}° · Konv ${f(d.frameCorrectionDeg, 3)}°`],
    ['See', `${x.lakeId ?? '–'} · ${x.drawnTriangles} Dreiecke`],
    ['FPS', f(x.fps, 0)],
  ];
}
